import React from 'react'
import { Paper, Chip, useTheme } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import PersonIcon from '@material-ui/icons/Person'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import EmailIcon from '@material-ui/icons/Email'
import {
    Lease,
    LeaseStatus,
    LeaseStatusType,
    getLeaseStatusString,
} from '../../models'
import { toMMDDYYYY } from '../../helpers'
import { typographyStyle } from '../../theme/typographyStyle'

interface LeaseCardProps {
    lease: Lease
}

const getStatusColor = (status: LeaseStatusType, theme: Theme) => {
    switch (status) {
        case LeaseStatus.CURRENT:
            return {
                bg: theme.palette.primary.main,
                text: theme.palette.common.white,
            }
        case LeaseStatus.FUTURE:
            return {
                bg: theme.palette.blue.main,
                text: theme.palette.common.white,
            }
        case LeaseStatus.NOTICE:
            return {
                bg: theme.palette.warning.main,
                text: theme.palette.common.white,
            }
        case LeaseStatus.PAST:
            return {
                bg: theme.palette.grey[500],
                text: theme.palette.common.white,
            }
        case LeaseStatus.CANCELLED:
            return {
                bg: theme.palette.error.main,
                text: theme.palette.common.white,
            }
        case LeaseStatus.APPLICANT:
            return {
                bg: theme.palette.purple.main,
                text: theme.palette.common.white,
            }
        default:
            return {
                bg: theme.palette.grey[400],
                text: theme.palette.common.white,
            }
    }
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        wrapper: {
            position: 'relative',
        },
        timelineDot: {
            position: 'absolute',
            left: -22,
            top: 20,
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: '0 0 0 2px ' + theme.palette.primary.main + '30',
            zIndex: 1,
        },
        card: {
            padding: theme.spacing(2.5),
            borderRadius: theme.spacing(1.5),
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.2s ease',
            '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: theme.palette.primary.light,
            },
        },
        header: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(2),
        },
        tenantInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
        },
        avatar: {
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.common.white,
            fontWeight: 600,
            fontSize: 16,
        },
        tenantDetails: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.25),
        },
        tenantName: {
            ...(typographyStyle(theme.typography.subtitle1) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            color: theme.palette.text.primary,
        },
        tenantEmail: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
        },
        statusChip: {
            fontWeight: 600,
            fontSize: 11,
            height: 24,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
        },
        datesContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(3),
            padding: theme.spacing(1.5),
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.spacing(1),
        },
        dateItem: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        dateIcon: {
            color: theme.palette.text.secondary,
            fontSize: 16,
        },
        dateContent: {
            display: 'flex',
            flexDirection: 'column',
        },
        dateLabel: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
        },
        dateValue: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            color: theme.palette.text.primary,
        },
        dateSeparator: {
            width: 1,
            height: 32,
            backgroundColor: theme.palette.divider,
        },
    }),
)

export const LeaseCard = ({ lease }: LeaseCardProps) => {
    const theme = useTheme()
    const classes = useStyles()

    const statusColors = getStatusColor(lease.status, theme)
    const startDate = new Date(lease.start_date)
    const endDate = new Date(lease.end_date)

    // Get initials from tenant name
    const getInitials = (name: string) => {
        const parts = name.split(' ')
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
        }
        return name.substring(0, 2).toUpperCase()
    }

    return (
        <div className={classes.wrapper}>
            <div
                className={classes.timelineDot}
                style={{ backgroundColor: statusColors.bg }}
            />
            <Paper className={classes.card} elevation={0}>
                <div className={classes.header}>
                    <div className={classes.tenantInfo}>
                        <div
                            className={classes.avatar}
                            style={{ backgroundColor: statusColors.bg }}
                        >
                            {getInitials(lease.user.name)}
                        </div>
                        <div className={classes.tenantDetails}>
                            <span className={classes.tenantName}>
                                {lease.user.name}
                            </span>
                            <span className={classes.tenantEmail}>
                                <EmailIcon style={{ fontSize: 12 }} />
                                {lease.user.email}
                            </span>
                        </div>
                    </div>
                    <Chip
                        label={getLeaseStatusString(lease.status)}
                        className={classes.statusChip}
                        style={{
                            backgroundColor: statusColors.bg,
                            color: statusColors.text,
                        }}
                        size="small"
                    />
                </div>

                <div className={classes.datesContainer}>
                    <div className={classes.dateItem}>
                        <CalendarTodayIcon className={classes.dateIcon} />
                        <div className={classes.dateContent}>
                            <span className={classes.dateLabel}>
                                Start Date
                            </span>
                            <span className={classes.dateValue}>
                                {toMMDDYYYY(startDate)}
                            </span>
                        </div>
                    </div>
                    <div className={classes.dateSeparator} />
                    <div className={classes.dateItem}>
                        <CalendarTodayIcon className={classes.dateIcon} />
                        <div className={classes.dateContent}>
                            <span className={classes.dateLabel}>End Date</span>
                            <span className={classes.dateValue}>
                                {toMMDDYYYY(endDate)}
                            </span>
                        </div>
                    </div>
                </div>
            </Paper>
        </div>
    )
}
